/**
 * Workspace preset store (R17 — Founder-mode navigation).
 *
 * The SERVER (user-service org settings) is the source of truth; the store is a
 * render cache so the sidebar reflects the org preset everywhere and updates
 * instantly after a switch in Settings → Organisation. The last known value is
 * cached in localStorage only to avoid a full-nav flash before the first fetch
 * resolves — it is always overwritten by the server value.
 */
import { create } from 'zustand';

import {
  DEFAULT_WORKSPACE_PRESET,
  getOrgSettings,
  isWorkspacePreset,
  type WorkspacePreset,
} from '@/lib/api/orgSettings';

export const WORKSPACE_PRESET_CACHE_KEY = 'karexia.workspacePreset';

function readCached(): WorkspacePreset {
  try {
    const raw = localStorage.getItem(WORKSPACE_PRESET_CACHE_KEY);
    if (isWorkspacePreset(raw)) return raw;
  } catch {
    /* storage disabled */
  }
  return DEFAULT_WORKSPACE_PRESET;
}

function writeCached(preset: WorkspacePreset): void {
  try {
    localStorage.setItem(WORKSPACE_PRESET_CACHE_KEY, preset);
  } catch {
    /* ignore */
  }
}

interface WorkspacePresetState {
  preset: WorkspacePreset;
  /** 'idle' until the first server fetch settles. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Fetch the org preset from the server (no-op while a fetch is in flight). */
  load: (force?: boolean) => Promise<void>;
  /** Apply a preset already persisted server-side (after a successful PATCH). */
  setPreset: (preset: WorkspacePreset) => void;
}

export const useWorkspacePresetStore = create<WorkspacePresetState>((set, get) => ({
  preset: readCached(),
  status: 'idle',
  load: async (force = false) => {
    const { status } = get();
    if (status === 'loading' || (status === 'ready' && !force)) return;
    set({ status: 'loading' });
    try {
      const s = await getOrgSettings('me');
      const preset = isWorkspacePreset(s.workspace_preset) ? s.workspace_preset : DEFAULT_WORKSPACE_PRESET;
      writeCached(preset);
      set({ preset, status: 'ready' });
    } catch {
      // Fail OPEN to the full navigation: an unreachable settings service must
      // never hide modules. Keep any cached value only when it is the default.
      set({ preset: DEFAULT_WORKSPACE_PRESET, status: 'error' });
    }
  },
  setPreset: (preset) => {
    writeCached(preset);
    set({ preset, status: 'ready' });
  },
}));
